/**
 * CreateJob - Mode Selector Page
 *
 * Entry point for creating new jobs/permits.
 * Presents two options:
 *   1. Guided Intake (Wizard) - Step-by-step for new users
 *   2. Quick Create - Document-first verification for experienced users
 */

import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, ClipboardList, Zap } from "lucide-react";

const CreateJob = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-30">
        <div className="flex items-center gap-4 h-14 px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/jobs")}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">Create New Permit</h1>
        </div>
      </header>

      {/* Mode Selector Dialog - Always open */}
      <Dialog
        open={true}
        onOpenChange={(open) => {
          if (!open) {
            // User clicked X or outside dialog - go back
            navigate(-1);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>How would you like to create your permit?</DialogTitle>
            <DialogDescription>
              Choose the method that works best for you.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Guided Intake Option */}
            <button
              onClick={() => navigate("/wizard")}
              className="flex items-start gap-4 p-4 rounded-lg border border-border bg-card/40 hover:bg-muted/40 hover:border-foreground/20 transition-colors text-left"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">Guided Intake</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Step-by-step wizard that helps determine required permits and collects all
                  necessary information.
                </p>
                <span className="inline-flex items-center mt-2 text-xs font-medium rounded-full border px-2.5 py-0.5 bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400">
                  Recommended for new users
                </span>
              </div>
            </button>

            {/* Quick Create Option */}
            <button
              onClick={() => navigate("/quick-create")}
              className="flex items-start gap-4 p-4 rounded-lg border border-border bg-card/40 hover:bg-muted/40 hover:border-foreground/20 transition-colors text-left"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Zap className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">Quick Create</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload documents and verify compliance in seconds. Best for contractors who know
                  exactly what they need.
                </p>
                <span className="inline-flex items-center mt-2 text-xs font-medium rounded-full border px-2.5 py-0.5 bg-sky-500/10 text-sky-600 border-sky-500/20 dark:text-sky-400">
                  Document-first verification
                </span>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateJob;
